/**
 * SBI Import operations service.
 * Provides async functions for importing fields via SBI number.
 */

import { QueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { fetchApi } from "common/utils/Api";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { ArableField, Field } from "model";
import { useAppStore, useUIStore } from "stores";
import { fetchFarmByIdRQ, initializeFarmRQ } from "./initializationRQ";

export type SBIImportPayload = {
  SBINumber: number;
  userGuid: string;
  farmGuid: string;
};

export type SBIFieldPreview = {
  sourceIndex: number;
  name: string;
  parcelId?: string;
  fieldType: "GRASS" | "ARABLE";
  geometry: Geometry;
};

export type SBIFieldImportSelection = {
  sourceIndex: number;
  include: boolean;
  name: string;
  fieldType: "GRASS" | "ARABLE";
  grassType?: string;
  arableType?: string;
};

export type SBIFieldImportResult = {
  sourceIndex: number;
  name: string;
  status: "imported" | "not_imported";
  fieldGuid?: string;
  reason?: string;
};

export type SBIPreviewResponse = {
  SBINumber: string;
  fields: SBIFieldPreview[];
  fieldsCount: number;
};

type SBIImportResponse = {
  farmGuid: string;
  fieldGuids: string[];
  fieldsCreated: number;
  fieldResults: SBIFieldImportResult[];
};

function fieldToFeature(
  field: Field | ArableField | undefined
): Feature | null {
  if (!field) {
    return null;
  }

  const { geom } = field;

  if (!geom?.geometry || !("coordinates" in geom.geometry)) {
    return null;
  }

  return {
    type: "Feature",
    geometry: geom.geometry,
    properties: {
      ...geom.properties,
      id: field.guid,
    },
  };
}

function getImportedFieldsGeom(
  queryClient: QueryClient,
  fieldGuids: string[]
): FeatureCollection {
  const features = fieldGuids.flatMap((fieldGuid) => {
    const field = queryClient.getQueryData<Field>(
      queryKeys.fields.detail(fieldGuid)
    );
    const arableField = queryClient.getQueryData<ArableField>(
      queryKeys.arableFields.detail(fieldGuid)
    );
    const feature = fieldToFeature(field ?? arableField);

    return feature ? [feature] : [];
  });

  return {
    type: "FeatureCollection",
    features,
  };
}

async function refreshImportedFarmCache(
  queryClient: QueryClient,
  farmId: string
): Promise<void> {
  queryClient.removeQueries({ queryKey: queryKeys.fields.byFarm(farmId) });
  queryClient.removeQueries({ queryKey: queryKeys.arableFields.ids(farmId) });
  queryClient.removeQueries({
    queryKey: queryKeys.arableFields.byFarm(farmId),
  });

  await queryClient.invalidateQueries({
    queryKey: queryKeys.farms.detail(farmId),
  });
  await queryClient.invalidateQueries({ queryKey: queryKeys.fields.all });
  await queryClient.invalidateQueries({ queryKey: queryKeys.arableFields.all });
}

export async function previewSBIFields(
  sbiNumber: string
): Promise<SBIPreviewResponse> {
  const response = (await fetchApi("SBIImport/preview", {
    method: "POST",
    body: JSON.stringify({
      SBINumber: Number(sbiNumber),
    }),
  })) as SBIPreviewResponse;

  return response;
}

/**
 * Import SBI fields to an existing farm.
 * Service function for SBI import operations.
 */
export async function importSBIToExistingFarm(
  {
    sbiNumber,
    farmGuid,
    fields,
  }: {
    sbiNumber: string;
    farmGuid: string;
    fields?: SBIFieldImportSelection[];
  },
  queryClient: QueryClient
): Promise<SBIImportResponse> {
  const user = useAppStore.getState().user;

  if (!user?.guid) {
    throw new Error("User not authenticated");
  }

  const response = (await fetchApi("SBIImport", {
    method: "POST",
    body: JSON.stringify({
      SBINumber: Number(sbiNumber),
      userGuid: user.guid,
      farmGuid,
      fields,
    }),
  })) as SBIImportResponse;

  // After import, refresh the farm data to get correct field categorization
  if (response.fieldGuids && response.fieldGuids.length > 0) {
    const farmId = response.farmGuid || useAppStore.getState().currentFarmId;

    if (farmId) {
      await refreshImportedFarmCache(queryClient, farmId);

      // Fetch the updated farm data (includes new field IDs)
      const updatedFarm = await fetchFarmByIdRQ(farmId);

      // Initialize the farm with the updated data
      await initializeFarmRQ(updatedFarm);
    }

    const importedFieldsGeom = getImportedFieldsGeom(
      queryClient,
      response.fieldGuids
    );

    if (importedFieldsGeom.features.length > 0) {
      useUIStore.getState().setZoomToGeom(importedFieldsGeom);
    }
  }

  return response;
}
